/**
 * Generates a complete, print-ready HTML catalogue document.
 *
 * Ported from frontend-sales/CataloguePage.tsx — `generateCatalogueHtml()`.
 *
 * Key changes vs original:
 * - Branding colours come from `CatalogueSettings` (not hardcoded Didier palette)
 *   so the same code works for any brand (CDD, LCDD, etc.)
 * - Font filenames resolved from `settings.font_files`
 * - Banner URL comes from API (SW caches it for offline use)
 * - Brand name / subtitle from settings
 */

import type { ItemRow, CatalogueSettings, AirlineTier } from '../shared/types';
import { DEFAULT_CATALOGUE_SETTINGS, AIRLINE_TIER_ORDER, AIRLINE_TIERS } from '../shared/types';
import { boldAllergens } from './boldAllergens';
import { getAllergens, getSuitability } from './allergenHelpers';
import { productPhotoUrl } from '../shared/services/photoCache';
import { API_BASE_URL } from '../shared/services/apiClient';

export type SupportedLang = 'en' | 'fr' | 'es';

type PdfLabel = {
  suitableFor: string;
  allergens: string;
  ingredients: string;
  defrostLife: string;
  handling: string;
  caseSize: string;
  weight: string;
  price: string;
  perUnit: string;
  days: string;
  productCatalogue: string;
  printButton: string;
  closeButton: string;
  allergenNames: Record<string, string>;
  suitabilityNames: Record<string, string>;
};

const PDF_LABELS: Record<SupportedLang, PdfLabel> = {
  en: {
    suitableFor: 'Suitable for:', allergens: 'Allergens:', ingredients: 'Ingredients:',
    defrostLife: 'Defrosted shelf life:', handling: 'Handling:',
    caseSize: 'Case Size:', weight: 'Weight:', price: 'Price:', perUnit: 'per unit', days: 'days',
    productCatalogue: 'Product Catalogue', printButton: 'Print / Save PDF', closeButton: 'Close',
    allergenNames: {
      Gluten: 'Gluten', Egg: 'Egg', Nuts: 'Nuts', Soya: 'Soya', Dairy: 'Dairy',
      Sesame: 'Sesame', Sulphites: 'Sulphites', Fish: 'Fish', Celery: 'Celery',
      Mustard: 'Mustard', Lupin: 'Lupin', Mollusc: 'Mollusc',
    },
    suitabilityNames: { Halal: 'Halal', Vegetarian: 'Vegetarian', Vegan: 'Vegan' },
  },
  fr: {
    suitableFor: 'Convient à :', allergens: 'Allergènes :', ingredients: 'Ingrédients :',
    defrostLife: 'Durée de vie décongelée :', handling: 'Manipulation :',
    caseSize: 'Taille caisse :', weight: 'Poids :', price: 'Prix :', perUnit: 'par unité', days: 'jours',
    productCatalogue: 'Catalogue Produits', printButton: 'Imprimer / Enregistrer en PDF', closeButton: 'Fermer',
    allergenNames: {
      Gluten: 'Gluten', Egg: 'Œufs', Nuts: 'Fruits à coque', Soya: 'Soja', Dairy: 'Lait',
      Sesame: 'Sésame', Sulphites: 'Anhydrides sulfureux', Fish: 'Poissons', Celery: 'Céleri',
      Mustard: 'Moutarde', Lupin: 'Lupin', Mollusc: 'Mollusques',
    },
    suitabilityNames: { Halal: 'Halal', Vegetarian: 'Végétarien', Vegan: 'Végétalien' },
  },
  es: {
    suitableFor: 'Apto para:', allergens: 'Alérgenos:', ingredients: 'Ingredientes:',
    defrostLife: 'Vida útil descongelada:', handling: 'Manipulación:',
    caseSize: 'Talla de caja:', weight: 'Peso:', price: 'Precio:', perUnit: 'por unidad', days: 'días',
    productCatalogue: 'Catálogo de Productos', printButton: 'Imprimir / Guardar PDF', closeButton: 'Cerrar',
    allergenNames: {
      Gluten: 'Gluten', Egg: 'Huevos', Nuts: 'Frutos de cáscara', Soya: 'Soja', Dairy: 'Leche',
      Sesame: 'Sésamo', Sulphites: 'Dióxido de azufre', Fish: 'Pescado', Celery: 'Apio',
      Mustard: 'Mostaza', Lupin: 'Altramuces', Mollusc: 'Moluscos',
    },
    suitabilityNames: { Halal: 'Halal', Vegetarian: 'Vegetariano', Vegan: 'Vegano' },
  },
};

function tierLabel(tier: AirlineTier): string {
  return AIRLINE_TIERS.find(t => t.value === tier)?.label ?? tier;
}

export function toProperCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/(?:^|\s|[-/])\S/g, match => match.toUpperCase());
}

/**
 * Some handling guidelines are stored with an internal authoring marker prefix,
 * e.g. "<Handling guideline from frozen:# Remove individual portion...". Strip
 * everything from a leading "<" up to and including the first "#", then trim.
 * Returns clean, display-ready plain text.
 */
export function cleanHandlingGuideline(raw?: string | null): string {
  if (!raw) return '';
  return raw.replace(/^\s*<[^#]*#/, '').trim();
}

/** Escape HTML-significant characters so plain text renders verbatim in the PDF. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Fetch an image as a blob, draw it onto a canvas at the target size,
 * and return a compressed JPEG data URL. Fetching as a blob avoids
 * canvas CORS tainting so toDataURL() always works.
 */
async function compressPhoto(url: string, maxW = 280, maxH = 280, quality = 0.65): Promise<string | null> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    const bmpUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('decode'));
        i.src = bmpUrl;
      });
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > maxW || h > maxH) {
        const scale = Math.min(maxW / w, maxH / h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', quality);
    } finally {
      URL.revokeObjectURL(bmpUrl);
    }
  } catch {
    return null;
  }
}

/**
 * Pre-compress all product photos for PDF generation.
 * Returns a Map of itemcode -> JPEG data URL.
 */
export async function compressPhotosForPdf(
  items: ItemRow[],
  onProgress?: (done: number, total: number) => void,
  photoCacheBust?: number | null,
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const codes = items.map(i => i.itemcode).filter((c): c is string => !!c);
  const BATCH = 6;
  for (let i = 0; i < codes.length; i += BATCH) {
    const batch = codes.slice(i, i + BATCH);
    const results = await Promise.all(
      batch.map(code => {
        const url = productPhotoUrl(code, photoCacheBust);
        return compressPhoto(url).then(dataUrl => ({ code, dataUrl }));
      })
    );
    for (const { code, dataUrl } of results) {
      if (dataUrl) map.set(code, dataUrl);
    }
    onProgress?.(Math.min(i + BATCH, codes.length), codes.length);
  }
  return map;
}

export function generateCatalogueHtml(
  items: ItemRow[],
  settings: CatalogueSettings = DEFAULT_CATALOGUE_SETTINGS,
  showPrices?: boolean,
  tierMap?: Map<number, AirlineTier>,
  includeIngredients: boolean = true,
  bannerDataUrl?: string | null,
  photoMap?: Map<string, string>,
  photoCacheBust?: number | null,
  includeDefrostLife: boolean = false,
  includeHandling: boolean = false,
  lang: SupportedLang = 'en',
): string {
  const L = PDF_LABELS[lang] ?? PDF_LABELS.en;
  const C = settings.colours;
  const ff = settings.font_files;
  const fontBase = `${API_BASE_URL}/api/branding/font`;
  const bannerUrlFallback = `${API_BASE_URL}/api/branding/catalogue-banner`;
  const bannerUrl = (bannerDataUrl && bannerDataUrl.startsWith('data:')) ? bannerDataUrl : bannerUrlFallback;

  // Sort: Tier (if enabled) -> FP Category (cat6) -> Description
  const sorted =
    tierMap && tierMap.size > 0
      ? [...items].sort((a, b) => {
          const tierA = AIRLINE_TIER_ORDER[tierMap.get(a.itemid) ?? 'economy'] ?? 99;
          const tierB = AIRLINE_TIER_ORDER[tierMap.get(b.itemid) ?? 'economy'] ?? 99;
          if (tierA !== tierB) return tierA - tierB;
          const catA = a.category6_description || 'Uncategorised';
          const catB = b.category6_description || 'Uncategorised';
          const catCmp = catA.localeCompare(catB);
          if (catCmp !== 0) return catCmp;
          return (a.itemdescription ?? '').localeCompare(b.itemdescription ?? '');
        })
      : [...items].sort((a, b) => {
          const catA = a.category6_description || 'Uncategorised';
          const catB = b.category6_description || 'Uncategorised';
          const catCmp = catA.localeCompare(catB);
          if (catCmp !== 0) return catCmp;
          return (a.itemdescription ?? '').localeCompare(b.itemdescription ?? '');
        });

  // Group items by Tier (if airline) then FP Category (cat6)
  const byGroup = new Map<string, ItemRow[]>();
  for (const item of sorted) {
    const tierPart =
      tierMap?.get(item.itemid) != null ? `${tierLabel(tierMap.get(item.itemid)!)} | ` : '';
    const cat = item.category6_description || 'Uncategorised';
    const key = tierPart + cat;
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key)!.push(item);
  }

  let productHtml = '';
  for (const [groupLabel, catItems] of byGroup) {
    productHtml += `<div class="category-heading">${groupLabel}</div>\n`;
    for (const item of catItems) {
      const code = item.itemcode ?? '';
      const description = item.itemdescription ?? '';
      const webTitle = item.item_marketingproducttitle || '';
      const displayName = webTitle || toProperCase(description);
      const marketingDesc = item.item_marketingdescription ?? '';
      const caseSize = item.pack_size != null ? `x${item.pack_size}` : '';
      const totalWeight =
        item.final_weight != null && item.final_weight > 0 ? item.final_weight : null;
      const packSize =
        item.pack_size != null && item.pack_size > 0 ? item.pack_size : null;
      const displayWeight =
        totalWeight != null && packSize != null ? totalWeight / packSize : totalWeight;
      const weightStr = displayWeight != null ? `${Math.round(displayWeight)}` : 'na';
      const weightPerUnit = totalWeight != null && packSize != null;
      const suitability = getSuitability(item).map(s => L.suitabilityNames[s] ?? s);
      const allergens = getAllergens(item).map(a => L.allergenNames[a] ?? a);
      const ingredients = includeIngredients ? (item.item_ingredientlist ?? '') : '';
      const defrostLife =
        includeDefrostLife && item.int_life_2 != null && item.int_life_2 > 0
          ? `${Math.round(item.int_life_2)} ${L.days}`
          : '';
      const handling = includeHandling ? cleanHandlingGuideline(item.item_handling_guideline) : '';
      const photoUrl = (photoMap && code && photoMap.has(code))
        ? photoMap.get(code)!
        : productPhotoUrl(code, photoCacheBust);
      const price = showPrices && item.item_webprice != null ? item.item_webprice : undefined;
      const tier = tierMap?.get(item.itemid);
      const tierLabelHtml = tier ? `<span class="tier-badge">${tierLabel(tier)}</span>` : '';

      productHtml += `
      <div class="product-card">
        <div class="card-top">
          <div class="card-photo">
            <img src="${photoUrl}" alt="${code}" onerror="this.style.display='none';" />
          </div>
          <div class="card-details">
            <h2 class="product-title">${tierLabelHtml} ${code} &nbsp;&nbsp; ${displayName}</h2>
            ${marketingDesc ? `<p class="marketing-desc">${marketingDesc}</p>` : ''}
            <table class="detail-table">
              ${suitability.length > 0 ? `<tr><td class="dt-label">${L.suitableFor}</td><td>${suitability.join(', ')}.</td></tr>` : ''}
              ${allergens.length > 0 ? `<tr><td class="dt-label">${L.allergens}</td><td class="allergen-text">${allergens.join(', ')}.</td></tr>` : ''}
              ${ingredients ? `<tr><td class="dt-label">${L.ingredients}</td><td class="ingredients-text">${boldAllergens(ingredients)}</td></tr>` : ''}
              ${defrostLife ? `<tr><td class="dt-label">${L.defrostLife}</td><td>${defrostLife}</td></tr>` : ''}
              ${handling ? `<tr><td class="dt-label">${L.handling}</td><td>${escapeHtml(handling)}</td></tr>` : ''}
            </table>
            <div class="specs-row">
              <span><strong>${L.caseSize}</strong> &nbsp;${caseSize}</span>
              ${displayWeight != null ? `<span><strong>${L.weight}</strong> &nbsp;${weightStr} &nbsp;g${weightPerUnit ? ` ${L.perUnit}` : ''}</span>` : ''}
              ${price != null ? `<span class="price-tag"><strong>${L.price}</strong> &nbsp;&pound;${price.toFixed(2)}</span>` : ''}
            </div>
          </div>
        </div>
      </div>`;
    }
  }

  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
  <meta charset="utf-8" />
  <title>${settings.brand_name} — Product Catalogue</title>
  <style>
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.light}') format('truetype');
      font-weight: 300; font-style: normal;
    }
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.regular}') format('truetype');
      font-weight: 400; font-style: normal;
    }
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.semibold}') format('truetype');
      font-weight: 600; font-style: normal;
    }
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.bold}') format('truetype');
      font-weight: 700; font-style: normal;
    }
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.italic}') format('truetype');
      font-weight: 400; font-style: italic;
    }
    @font-face {
      font-family: '${settings.font_family}';
      src: url('${fontBase}/${ff.bold_italic}') format('truetype');
      font-weight: 700; font-style: italic;
    }
    @page { margin: 12mm 15mm 18mm 15mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: '${settings.font_family}', 'Segoe UI', Calibri, Arial, sans-serif;
      color: ${C.primary_dark}; font-size: 10pt; line-height: 1.4; background: #fff;
    }
    .catalogue-header { width: 100%; margin-bottom: 4px; page-break-after: avoid; }
    .catalogue-header img { width: 100%; height: auto; display: block; }
    .category-heading {
      background: ${C.primary_purple}; color: #fff;
      font-family: '${settings.font_family}', sans-serif;
      font-size: 12pt; font-weight: 600;
      padding: 6px 16px; margin: 16px 0 10px;
      letter-spacing: 0.5px; page-break-after: avoid;
    }
    .product-card {
      border: 1px solid ${C.supplementary_grey};
      padding: 12px 14px; margin-bottom: 12px;
      page-break-inside: avoid; background: #fff;
    }
    .card-top { display: flex; gap: 14px; align-items: flex-start; }
    .card-photo {
      width: 140px; min-height: 100px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .card-photo img { max-width: 140px; max-height: 140px; object-fit: contain; border-radius: 3px; }
    .card-details { flex: 1; min-width: 0; }
    .product-title {
      font-family: '${settings.font_family}', sans-serif;
      font-size: 13pt; font-weight: 600; color: ${C.primary_dark};
      margin: 0 0 4px; border-bottom: 1px solid ${C.supplementary_lavender}; padding-bottom: 4px;
    }
    .marketing-desc { font-size: 9.5pt; color: #555; margin: 4px 0 8px; font-style: italic; }
    .detail-table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-bottom: 6px; }
    .detail-table td { vertical-align: top; padding: 2px 0; }
    .dt-label { width: 90px; font-weight: 600; color: ${C.primary_dark}; white-space: nowrap; padding-right: 8px; }
    .allergen-text { color: ${C.primary_purple}; }
    .ingredients-text { color: #333; line-height: 1.4; }
    .ingredients-text strong { font-weight: 700; }
    .specs-row {
      display: flex; gap: 30px; font-size: 9.5pt; color: #666;
      margin-top: 6px; padding-top: 4px; border-top: 1px solid ${C.supplementary_lavender};
    }
    .price-tag { color: ${C.primary_purple}; font-weight: 600; margin-left: auto; }
    .tier-badge {
      display: inline-block; background: ${C.primary_gold}; color: ${C.primary_dark};
      font-size: 8pt; font-weight: 700; padding: 2px 6px; border-radius: 3px; margin-right: 6px;
    }
    .page-footer {
      display: flex; justify-content: space-between;
      font-size: 8pt; color: ${C.supplementary_grey};
      border-top: 2px solid ${C.primary_purple};
      padding-top: 8px; margin-top: 24px;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div id="print-toolbar" class="no-print" style="
    position:fixed;top:0;left:0;right:0;z-index:9999;
    background:${C.primary_dark};color:#fff;padding:8px 16px;
    display:flex;align-items:center;gap:12px;
    font-family:'${settings.font_family}',Arial,sans-serif;font-size:14px;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  ">
    <span style="font-weight:600;flex:1;">${settings.brand_name} \u2014 ${L.productCatalogue} (${items.length})</span>
    <button onclick="document.getElementById('print-toolbar').style.display='none';window.print();document.getElementById('print-toolbar').style.display='flex';" style="
      background:${C.primary_pink};color:${C.primary_dark};border:none;padding:6px 20px;
      border-radius:4px;font-weight:700;font-size:14px;cursor:pointer;
      font-family:'${settings.font_family}',sans-serif;
    ">${L.printButton}</button>
    <button onclick="window.close();" style="
      background:transparent;color:#fff;border:1px solid ${C.primary_pink};padding:6px 16px;
      border-radius:4px;font-size:14px;cursor:pointer;
      font-family:'${settings.font_family}',sans-serif;
    ">${L.closeButton}</button>
  </div>
  <div style="height:48px;" class="no-print"></div>

  <div class="catalogue-header">
    <img src="${bannerUrl}" alt="${settings.brand_name} — Product Catalogue" />
  </div>

  ${productHtml}

  <div class="page-footer">
    <span>${dateStr}</span>
    <span>${settings.brand_name} \u2022 ${L.productCatalogue} \u2022 ${items.length}</span>
  </div>
</body>
</html>`;
}
